<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDeviceStore } from '@/stores/device'

const { t } = useI18n()
const deviceStore = useDeviceStore()

// 档位位置映射（百分比位置）- 1: 0%, 2: 20%, 3: 40%, 4: 60%, 5: 80%, 6: 100%
const positions: Record<number, number> = {
  1: 0.0,
  2: 0.2,
  3: 0.4,
  4: 0.6,
  5: 0.8,
  6: 1.0,
}

const minLevel = 1
const maxLevel = 6
const totalLevels = 6

const isDragging = ref(false)
const trackRef = ref<HTMLElement | null>(null)

const toggleNoise = (val: boolean) => {
  deviceStore.setNoiseEnabled(val)
}

// 设置档位值
const setLevel = (level: number) => {
  const clamped = Math.min(Math.max(level, minLevel), maxLevel)
  deviceStore.setNoiseLevel(clamped)
}

// 按钮调节
const updateLevel = (delta: number) => {
  setLevel(deviceStore.noiseLevel + delta)
}

// 点击刻度直接跳转
const onTickClick = (value: number) => {
  if (!deviceStore.noiseEnabled) return
  setLevel(value)
}

// 根据位置计算最近的档位
const getClosestValueFromPosition = (pos: number): number => {
  let closestValue = 1
  let minDistance = Infinity

  for (let [value, valuePos] of Object.entries(positions)) {
    const distance = Math.abs(pos - valuePos)
    if (distance < minDistance) {
      minDistance = distance
      closestValue = parseInt(value)
    }
  }

  return closestValue
}

// 开始拖动
const onThumbMouseDown = (e: MouseEvent) => {
  if (!deviceStore.noiseEnabled) return
  e.preventDefault()
  isDragging.value = true
  document.body.style.cursor = 'grabbing'
}

// 拖动过程中
const onMouseMove = (e: MouseEvent) => {
  if (!isDragging.value || !deviceStore.noiseEnabled || !trackRef.value) return

  const trackRect = trackRef.value.getBoundingClientRect()
  const pos = (e.clientX - trackRect.left) / trackRect.width

  const closestValue = getClosestValueFromPosition(pos)

  if (deviceStore.noiseLevel !== closestValue) {
    deviceStore.setNoiseLevel(closestValue)
  }
}

// 结束拖动
const onMouseUp = () => {
  if (isDragging.value) {
    isDragging.value = false
    document.body.style.cursor = ''
  }
}

// 点击轨道跳转
const onTrackClick = (e: MouseEvent) => {
  if (!deviceStore.noiseEnabled || !trackRef.value) return

  const trackRect = trackRef.value.getBoundingClientRect()
  const pos = (e.clientX - trackRect.left) / trackRef.value.clientWidth

  const closestValue = getClosestValueFromPosition(pos)
  setLevel(closestValue)
}

// 计算进度条宽度百分比
const progressWidth = computed(() => {
  const pos = positions[deviceStore.noiseLevel] || 0
  return pos * 100 + '%'
})

const emit = defineEmits<{
  (e: 'close'): void
}>()

const closePopup = () => {
  emit('close')
}

// 全局鼠标事件
onMounted(() => {
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
})

onUnmounted(() => {
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
})
</script>

<template>
  <div class="noise-popup">
    <!-- 标题栏 -->
    <div class="popup-header">
      <div class="header-left">
        <span class="title-mic">通话降噪</span>
        <span class="title-headphone">AI降噪</span>
        <el-switch 
          v-model="deviceStore.noiseEnabled" 
          @change="toggleNoise"
          class="custom-switch"
        />
      </div>
      <div class="close-btn" @click="closePopup">
        <img src="/img/nav/nav_popup_closebtn.png" alt="close">
      </div>
    </div>

    <!-- 分隔线 -->
    <div class="divider"></div>

    <!-- 内容区 -->
    <div class="popup-body" :class="{ disabled: !deviceStore.noiseEnabled }">
      <div class="section-label">规格调节</div>
      
      <div class="slider-container">
        <button 
          class="slider-button" 
          id="decrement"
          @click="updateLevel(-1)" 
          :disabled="deviceStore.noiseLevel <= minLevel"
        >-</button>

        <div class="slider-wrapper">
          <div 
            ref="trackRef"
            class="slider-track" 
            @click="onTrackClick"
          >
            <div class="slider-progress" :style="{ width: progressWidth }"></div>
            <div class="slider-ticks">
              <span 
                v-for="i in totalLevels" 
                :key="i" 
                class="tick" 
                :data-value="i"
                @click.stop="onTickClick(i)"
              ></span>
            </div>
            <div 
              class="slider-thumb" 
              :style="{ left: progressWidth }"
              :class="{ grabbing: isDragging }"
              @mousedown="onThumbMouseDown"
            ></div>
          </div>
        </div>

        <button 
          class="slider-button" 
          id="increment"
          @click="updateLevel(1)" 
          :disabled="deviceStore.noiseLevel >= maxLevel"
        >+</button>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.noise-popup {
  width: 450px;
  border: 1px solid #2F2F37;
  border-radius: 10px;
  overflow: hidden;
  color: #fff;
  user-select: none;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;

  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;

    .title-mic,
    .title-headphone {
      font-size: 15px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.9);
    }
  }

  .close-btn {
    width: 22px;
    height: 22px;
    cursor: pointer;
    
    img {
      width: 100%;
      height: 100%;
    }

    &:hover {
      content: url('/img/nav/nav_popup_closebtn_hover.png');
    }
  }
}

.divider {
  width: 100%;
  height: 1px;
  background: #2F2F37;
}

.popup-body {
  padding: 20px;
  transition: opacity 0.3s;
  
  &.disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  .section-label {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 16px;
  }
}

.slider-container {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 20px;
  background: #1C1C21;
  border-radius: 10px;

  .slider-button {
    width: 35px;
    height: 35px;
    border-radius: 50%;
    border: none;
    background: rgba(0, 0, 0, 0.4);
    color: rgba(255, 255, 255, 0.9);
    font-size: 20px;
    font-weight: 300;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    flex-shrink: 0;
    line-height: 1;

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.1);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  #decrement {
    padding-bottom: 4px;
    font-size: 24px;
    font-weight: 200;
  }

  #increment {
    padding-bottom: 2px;
  }
}

.slider-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
}

.slider-track {
  flex: 1;
  height: 15px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  position: relative;
  cursor: pointer;
  user-select: none;

  &[disabled] {
    background-color: #dcdcdc;
  }
}

.slider-progress {
  position: absolute;
  height: 100%;
  background-color: #376BED;
  border-radius: 8px;
  top: 0;
  left: 0;
  transition: width 0.15s ease-out;
}

.slider-ticks {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 2px;
  pointer-events: none;

  .tick {
    width: 2.5px;
    height: 10px;
    background-color: rgba(255, 255, 255, 0.3);
    pointer-events: auto;
    cursor: pointer;
    align-self: center;

    &:hover::after {
      opacity: 1;
    }

    &::after {
      content: attr(data-value);
      position: absolute;
      top: 15px;
      left: 50%;
      transform: translateX(-50%);
      font-size: 10px;
      color: rgba(255, 255, 255, 0.7);
      opacity: 0;
      transition: opacity 0.3s;
    }
  }

  // 第一个和最后一个刻度隐藏
  :nth-child(1),
  :nth-last-child(1) {
    background-color: transparent;
    height: 2px;
  }
}

.slider-thumb {
  position: absolute;
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: left 0.15s ease-out, transform 0.15s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  z-index: 10;
  cursor: grab;
  user-select: none;

  &:hover {
    box-shadow: 0 0 0 8px rgba(255, 255, 255, 0.2);
  }

  &:active,
  &.grabbing {
    cursor: grabbing;
    transform: translate(-50%, -50%) scale(1.1);
  }

  &[disabled] {
    background-color: #a9a9a9;
    border-color: #b0b0b0;
  }
}

// 深度修改 Element Plus Switch 样式
:deep(.custom-switch) {
  .el-switch__core {
    background-color: #2a2a2a !important;
    border: none;
    height: 22px;
    width: 40px !important;
    
    .el-switch__action {
      background-color: #888;
      width: 16px;
      height: 16px;
    }
  }
  
  &.is-checked .el-switch__core {
    background-color: #4e75ff !important;
    
    .el-switch__action {
      background-color: #fff;
    }
  }
}
</style>